<template>
    <div class="appointment-calendar appointment-calendar-modern">
      <!-- Use CommonOverlay for loading state -->
      <CommonOverlay :show="isLoading" />

      <!-- === TEST WIDGET BUTTON (Temporary) === -->
      <div class="container text-center my-3">
        <b-button variant="warning" @click="simulateWidgetClick">
          Simulate Click from Dentist Website (Plugin ID)
        </b-button>
        <small class="d-block text-muted">(Reloads page with test plugin ID)</small>
      </div>
      <!-- ====================================== -->

      <!-- === Configuration Error Message === -->
      <div v-if="showConfigurationError" class="container configuration-error">
        <div class="error-icon">⚠️</div>
        <h2>Configuration Issue</h2>
        <p>
          {{ configErrorMessage }}
        </p>
        <p class="text-muted">
          (If you are the office administrator, please check your plugin settings and domain configuration in TeamIO.)
        </p>
      </div>
      <!-- =================================== -->

      <!-- Display general error message if present (e.g., API fetch error) -->
      <div v-if="getError && !showConfigurationError" class="container">
        <div class="calendar-error-message">
          {{ getError }}
        </div>
      </div>

      <!-- Main Calendar Content (Conditionally Rendered) -->
      <template v-if="!showConfigurationError && effectiveSiteId">
        <!-- Enhanced Office Header -->
        <div class="office-header">
          <div class="container">
            <div class="header-content">
              <div class="office-info">
                <div class="logo-container">
                  <div class="logo">
                    <span class="logo-icon">🦷</span>
                  </div>
                </div>
                <div class="text-content">
                  <h1 class="office-name">Mint Dental</h1>
                  <p class="office-tagline">Professional care for your perfect smile</p>
                </div>
              </div>
              <h2 class="page-title">Book an Appointment</h2>
              <div class="patient-card">
                <div class="patient-avatar">A</div>
                <div class="patient-info">
                  <p class="patient-name">John Smith</p>
                  <p class="patient-status">Active Patient</p>
                </div>
              </div>
              <!-- Admin Button (Simple version) -->
              <div class="admin-link">
                <router-link :to="{ name: 'CalendarAdminDashboard' }" class="admin-button">
                  <span class="admin-icon">⚙️</span>
                  <span class="admin-text">Admin Dashboard</span>
                </router-link>
              </div>
            </div>
          </div>
        </div>
  
        <!-- Calendar Container -->
        <div class="calendar-container">
          <div class="container">
            <div class="calendar-card">
              <!-- Calendar Controls -->
              <div class="calendar-controls">
                <div class="navigation-controls">
                  <button class="btn btn-icon" @click="prevWeek">
                    <span class="icon">←</span>
                  </button>
                  <h2 class="current-month-year">{{ currentMonthYearDisplay }}</h2>
                  <button class="btn btn-icon" @click="nextWeek">
                    <span class="icon">→</span>
                  </button>
                </div>
                <div class="calendar-actions">
                  <button class="btn btn-primary today-btn" @click="goToToday">Today</button>
                  <div class="date-picker-wrapper">
                    <input
                      type="date"
                      class="date-picker-input"
                      :value="currentDateISO"
                      @change="jumpToDate($event)"
                      title="Jump to date"
                    />
                    <button class="btn btn-icon date-picker-btn" title="Jump to date">
                      <span class="icon">📅</span>
                    </button>
                  </div>
                </div>
                <div class="view-controls">
                  <button
                    class="btn"
                    :class="{ 'btn-active': currentView === 'month' }"
                    @click="changeView('month')"
                  >Month</button>
                  <button
                    class="btn"
                    :class="{ 'btn-active': currentView === 'week' }"
                    @click="changeView('week')"
                  >Week</button>
                  <button
                    class="btn"
                    :class="{ 'btn-active': currentView === 'day' }"
                    @click="changeView('day')"
                  >Day</button>
                </div>
              </div>
              
              <!-- Simple Calendar (Day/Week View) -->
              <div v-if="currentView === 'day' || currentView === 'week'" class="simple-calendar">
                <!-- Calendar Header -->
                <div class="calendar-header">
                  <div v-for="day in visibleDateRange" :key="day.date" class="calendar-day-header">
                    <div class="day-name">{{ day.dayName }}</div>
                    <div class="day-date">{{ day.dayDate }}</div>
                  </div>
                </div>
                
                <!-- Calendar Body -->
                <div class="calendar-body">
                  <div class="time-slots">
                    <div v-for="increment in timeIncrements" :key="increment.minutes" class="time-label" :class="{ 'half-hour': increment.minutes % 60 !== 0 }">
                      {{ increment.hourLabel }}
                    </div>
                  </div>
                  
                  <div class="days-grid">
                    <div v-for="day in visibleDateRange" :key="day.date" class="day-column">
                      <div 
                        v-for="slot in getSlotsForDay(day)" 
                        :key="slot.time" 
                        class="time-slot"
                        :class="{ 
                          'available': slot.status === 'available',
                          'booked': slot.status === 'booked'
                        }"
                        @click="slot.status === 'available' ? selectSlot(day, slot) : null"
                      >
                        <div v-if="slot.status === 'available'" class="slot-content">
                          <span class="slot-doctor">{{ slot.label }}</span>
                          <span class="slot-time">{{ slot.startTime }} - {{ slot.endTime }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Month View Grid -->
              <div v-else-if="currentView === 'month'" class="month-calendar">
                 <!-- Month Calendar Header (Days of Week) -->
                 <div class="month-header">
                     <div v-for="dayName in monthDayNames" :key="dayName" class="month-day-name">
                         {{ dayName }}
                     </div>
                 </div>
                 <!-- Month Grid Body -->
                 <div class="month-grid">
                     <div v-for="(week, index) in monthGridData" :key="index" class="month-week">
                         <div 
                             v-for="day in week" 
                             :key="day.date" 
                             class="month-day"
                             :class="{ 
                                 'is-today': day.isToday, 
                                 'not-current-month': !day.isCurrentMonth,
                                 'has-availability': day.hasAvailability /* Placeholder */ 
                             }"
                             @click="selectMonthDay(day)"
                         >
                             <span class="day-number">{{ day.dayNumber }}</span>
                             <!-- Display list of slots -->
                             <div class="month-day-slots" v-if="day.isCurrentMonth && day.hasAvailability">
                                <div v-for="(slotText, index) in day.slotsList" :key="index" class="month-slot-item">
                                  {{ slotText }}
                                </div>
                             </div>
                         </div>
                     </div>
                 </div>
              </div>

            </div>
          </div>
        </div>
  
        <!-- Updated Booking Confirmation Modal using b-modal (matching OverdueRecall) -->
        <b-modal 
          id="booking-confirmation-modal"
          ref="booking-confirmation-modal"
          v-model="showBookingModal" 
          title="Book Your Dental Appointment"
          body-class="bg-white mb-0"
          centered
        >
          <!-- Modal Body -->
          <!-- Date and Time Section -->
          <div class="appointment-datetime">
            <div class="datetime-item">
              <div class="datetime-icon">
                <span class="calendar-icon">📅</span>
              </div>
              <div class="datetime-text">
                {{ selectedSlot ? `${selectedSlot.dayName}, ${selectedSlot.dayDate}, ${new Date().getFullYear()}` : '' }}
              </div>
            </div>
            <div class="datetime-item">
              <div class="datetime-icon">
                <span class="time-icon">🕒</span>
              </div>
              <div class="datetime-text">
                {{ selectedSlot ? `${selectedSlot.startTime} - ${selectedSlot.endTime}` : '' }}
              </div>
            </div>
          </div>
          
          <!-- Form Section -->
          <div class="appointment-form">
            <div class="form-row">
              <div class="form-group">
                <label for="firstName">First Name</label>
                <input type="text" id="firstName" class="form-control" placeholder="Enter your first name" v-model="bookingForm.firstName" :class="{ 'is-invalid': !validations.firstName }" @input="validations.firstName = true">
                <small v-if="!validations.firstName" class="text-danger">First name is required.</small>
              </div>
              <div class="form-group">
                <label for="lastName">Last Name</label>
                <input type="text" id="lastName" class="form-control" placeholder="Enter your last name" v-model="bookingForm.lastName" :class="{ 'is-invalid': !validations.lastName }" @input="validations.lastName = true">
                <small v-if="!validations.lastName" class="text-danger">Last name is required.</small>
              </div>
            </div>
            
            <div class="form-row">
              <div class="form-group">
                <label for="email">Email</label>
                <input type="email" id="email" class="form-control" placeholder="your.email@example.com" v-model="bookingForm.email" :class="{ 'is-invalid': !validations.email }" @input="validations.email = true">
                <small v-if="!validations.email" class="text-danger">Valid email is required.</small>
              </div>
              <div class="form-group">
                <label for="phone">Phone Number</label>
                <input type="tel" id="phone" class="form-control" placeholder="(555) 123-4567" v-model="bookingForm.phone" :class="{ 'is-invalid': !validations.phone }" @input="validations.phone = true">
                <small v-if="!validations.phone" class="text-danger">Phone number is required.</small>
              </div>
            </div>
            
            <div class="form-row">
              <div class="form-group">
                <label for="treatment">Treatment</label>
                <select id="treatment" class="form-control" v-model="bookingForm.treatment" :class="{ 'is-invalid': !validations.treatment }" @change="validations.treatment = true">
                  <option value="" disabled selected>Select treatment</option>
                  <!-- Dynamically generate options from configuration if available -->
                  <template v-if="pluginConfiguration && pluginConfiguration.treatments && pluginConfiguration.treatments.length">
                    <option 
                      v-for="treatment in pluginConfiguration.treatments" 
                      :key="treatment.id" 
                      :value="treatment.id"
                    >
                      {{ treatment.name }} ({{ treatment.duration }} min)
                    </option>
                  </template>
                  <!-- Fallback options if no configuration is available -->
                  <template v-else>
                    <option value="cleaning">Regular Cleaning</option>
                    <option value="whitening">Teeth Whitening</option>
                    <option value="filling">Dental Filling</option>
                    <option value="extraction">Tooth Extraction</option>
                    <option value="rootcanal">Root Canal</option>
                    <option value="consultation">Consultation</option>
                  </template>
                </select>
                <small v-if="!validations.treatment" class="text-danger">Treatment selection is required.</small>
                <!-- Show treatment duration if available -->
                <small v-if="selectedTreatmentDuration" class="text-info mt-1">
                  <i class="fa fa-clock-o"></i> Duration: {{ selectedTreatmentDuration }} minutes
                </small>
              </div>
              <div class="form-group">
                <label for="dentist">Dentist</label>
                <select id="dentist" class="form-control" v-model="bookingForm.dentist">
                  <option value="" disabled selected>Select dentist</option>
                  <option value="smith">Dr. Smith</option>
                  <option value="johnson">Dr. Johnson</option>
                  <option value="williams">Dr. Williams</option>
                  <option value="brown">Dr. Brown</option>
                </select>
              </div>
            </div>
            
            <div class="form-group">
              <label for="notes">Additional Notes</label>
              <textarea id="notes" class="form-control" rows="4" placeholder="Any specific concerns or additional information..." v-model="bookingForm.notes"></textarea>
            </div>
          </div>
          
          <!-- Modal Footer -->
          <template #modal-footer>
              <b-button variant="secondary" size="sm" @click="closeModal">Cancel</b-button>
              <b-button variant="primary" size="sm" class="book-btn" @click="confirmBooking">Book Appointment</b-button>
          </template>
        </b-modal>
  
        <!-- REMOVED Success Message -->
        <!-- 
        <div v-if="showSuccessMessage" class="success-message">
          <div class="container">
            <div class="success-content">
              <div class="success-icon">✓</div>
              <div class="success-text">
                <h3>Appointment Confirmed!</h3>
                <p>Your appointment has been successfully booked.</p>
              </div>
              <button class="close-btn" @click="showSuccessMessage = false" aria-label="Close">×</button>
            </div>
          </div>
        </div> 
        -->
      </template> <!-- End of v-if="!showConfigurationError && effectiveSiteId" -->
    </div>
  </template>
  
  <script>
  import { mapState, mapGetters, mapActions } from 'vuex';
  import moment from 'moment-timezone';
  import CommonOverlay from '@/components/CommonOverlay.vue';

  export default {
    name: 'AppointmentCalendar',
    components: {
      CommonOverlay
    },
    data() {
      return {
        // Configuration & Metadata
        currentView: 'week', // 'day', 'week', or 'month'
        currentDate: new Date(), // Defaults to today
        // Default configs for demo
        businessHours: [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19],
        slotIncrementMinutes: 30, // Default value - will be updated from configuration if available
        dayStartTimeHour: 7,
        dayEndTimeHour: 20, // Represents up to 7:59 PM
        
        // Modal Control
        showBookingModal: false,
        selectedSlot: null,
        showTimeList: false, // For small screen alternative

        // Form Data
        bookingForm: {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          treatment: '',
          dentist: '',
          notes: ''
        },
        // --- NEW: Validation state ---
        validations: {
          firstName: true,
          lastName: true,
          email: true,
          phone: true,
          treatment: true
        },
        // --- NEW: Store site ID determined by logic ---
        effectiveSiteId: null,
        // --- NEW: Flag to show config error ---
        showConfigurationError: false,
        // --- NEW: Custom error message ---
        configErrorMessage: 'There seems to be an issue loading the appointment calendar for this office. Please ensure the booking link or plugin is correctly configured.',
        // --- NEW: Track timezone ---
        timezoneSet: false,
        
        // Month view variables
        // Month view variables are computed; no data defaults needed here
      };
    },
    computed: {
      ...mapGetters('calendar', [
        'isLoading',
        'getError',
        'getAvailabilityForDate',
        'getSiteInfo',
        'getSiteTimezone'
      ]),
      
      // Get the site's timezone from settings
      siteTimezone() {
        return this.getSiteTimezone;
      },

      // Display current month and year based on view
      currentMonthYearDisplay() {
        const date = moment(this.currentDate);
        return date.format('MMMM YYYY');
      },

      // ISO format of current date for the date picker input
      currentDateISO() {
        return moment(this.currentDate).format('YYYY-MM-DD');
      },

      // NEW: Get the plugin configuration
      pluginConfiguration() {
        const siteInfo = this.getSiteInfo;
        if (siteInfo && siteInfo.Configuration) {
          try {
            // If it's already parsed as an object
            if (typeof siteInfo.Configuration === 'object') {
              return siteInfo.Configuration;
            }
            // If it's a JSON string
            return JSON.parse(siteInfo.Configuration);
          } catch (e) {
            console.error('Error parsing plugin configuration:', e);
          }
        }
        return null;
      },
      
      // NEW: Get configured slot duration or default to 30
      configuredSlotDuration() {
        if (this.pluginConfiguration && 
            this.pluginConfiguration.calendar && 
            this.pluginConfiguration.calendar.slotDurationMinutes) {
          return this.pluginConfiguration.calendar.slotDurationMinutes;
        }
        return 30; // Default value
      },

      // NEW: Get the duration of the selected treatment
      selectedTreatmentDuration() {
        if (!this.bookingForm.treatment || !this.pluginConfiguration || !this.pluginConfiguration.treatments) {
          return null;
        }
        
        const selectedTreatment = this.pluginConfiguration.treatments.find(t => t.id === this.bookingForm.treatment);
        return selectedTreatment ? selectedTreatment.duration : null;
      },

      // Generate time points based on start/end times and increment
      timeIncrements() {
        const increments = [];
        for (let hour = this.dayStartTimeHour; hour < this.dayEndTimeHour; hour++) {
          // Add hour marker
          increments.push({
            hour: hour,
            minutes: hour * 60,
            formatted: this.formatHour(hour),
            hourLabel: this.formatHour(hour)
          });
          
          // Add half-hour marker
          increments.push({
            hour: hour,
            minutes: hour * 60 + 30,
            formatted: `${this.formatHour(hour)}:30`,
            hourLabel: ''
          });
        }
        return increments;
      },

      visibleDateRange() {
        // Generate date array for current view
        const dateArray = [];
        const startDate = this.getStartDate();
        
        // Number of days to display based on view
        let numDays = 1; // Default to day view
        if (this.currentView === 'week') {
          numDays = 7; // Week view
        }
        
        const formattedToday = moment().format('YYYY-MM-DD');
        
        for (let i = 0; i < numDays; i++) {
          const currentDate = moment(startDate).add(i, 'days');
          const dateISO = currentDate.format('YYYY-MM-DD');
          const isToday = dateISO === formattedToday;
          
          dateArray.push({
            date: dateISO,
            dayName: this.getDayName(currentDate, i === 0), // Full name for first day
            dayDate: this.formatDate(currentDate),
            isToday: isToday
          });
        }
        
        return dateArray;
      },

      // --- Re-added: Month View Grid Data ---
      monthGridData() {
          const weeks = [];
          const currentMonth = this.currentDate.getMonth();
          const currentYear = this.currentDate.getFullYear();
          const firstDayOfMonth = moment([currentYear, currentMonth, 1]);
          const daysInMonth = firstDayOfMonth.daysInMonth();
          const firstDayWeekday = firstDayOfMonth.day(); // 0=Sun, 1=Mon, ...

          let currentDay = moment(firstDayOfMonth).subtract(firstDayWeekday, 'days');
          const todayISO = moment().format('YYYY-MM-DD');

          for (let w = 0; w < 6; w++) { // Always generate 6 weeks for consistent layout
              const week = [];
              for (let d = 0; d < 7; d++) {
                  const dateISO = currentDay.format('YYYY-MM-DD');
                  // Check for *any* availability on this date
                  // const availability = this.getAvailabilityForDate(dateISO);
                  // const hasAvailability = !!availability; 
                  // Get formatted slot list instead
                  const slotsList = this.getFormattedSlotsForDay(dateISO);
                  const hasAvailability = slotsList.length > 0;

                  week.push({
                      date: dateISO,
                      dayNumber: currentDay.date(),
                      isCurrentMonth: currentDay.month() === currentMonth,
                      isToday: dateISO === todayISO,
                      hasAvailability: hasAvailability, 
                      slotsList: slotsList // Store the list
                  });
                  currentDay.add(1, 'day');
              }
              weeks.push(week);
          }
          return weeks;
      },
      
      // --- Re-added: Day Names for Month Header ---
      monthDayNames() {
          return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      },

      // Check if authenticated for admin access
      isAuthenticated() {
        return localStorage.getItem('authenticated') === 'true';
      }
    },
    methods: {
      ...mapActions('calendar', [
        'fetchAvailability',
        'fetchSiteInfoByPluginId'
      ]),
      
      // --- Initialize timezone for moment.js ---
      initializeMomentTimezone() {
        if (this.timezoneSet) return;
        
        const timezone = this.siteTimezone;
        // console.log(`TIMEZONE: Set to ${timezone}`);
        moment.tz.setDefault(timezone);
        this.timezoneSet = true;
      },

      // --- NEW: Apply plugin configuration settings ---
      applyPluginConfiguration() {
        if (!this.pluginConfiguration) {
          // console.log('No plugin configuration found, using defaults');
          return;
        }
        
        // console.log('Applying plugin configuration:', this.pluginConfiguration);
        
        // Apply calendar settings
        if (this.pluginConfiguration.calendar) {
          const calConfig = this.pluginConfiguration.calendar;
          
          // Update slot duration
          if (calConfig.slotDurationMinutes) {
            this.slotIncrementMinutes = calConfig.slotDurationMinutes;
            // console.log(`CONFIG: Slot duration set to ${this.slotIncrementMinutes} minutes`);
          }
          
          // Update business hours
          if (calConfig.businessHours) {
            if (calConfig.businessHours.startHour !== undefined) {
              this.dayStartTimeHour = calConfig.businessHours.startHour;
            }
            if (calConfig.businessHours.endHour !== undefined) {
              this.dayEndTimeHour = calConfig.businessHours.endHour;
            }
            // console.log(`CONFIG: Business hours set to ${this.dayStartTimeHour}:00 - ${this.dayEndTimeHour}:00`);
          }
        }
        
        // Apply UI settings
        if (this.pluginConfiguration.ui) {
          const uiConfig = this.pluginConfiguration.ui;
          
          // Set default view
          if (uiConfig.defaultView && ['day', 'week', 'month'].includes(uiConfig.defaultView)) {
            this.currentView = uiConfig.defaultView;
            // console.log(`CONFIG: Default view set to ${this.currentView}`);
          }
        }
        
        // Apply treatment options if present
        if (this.pluginConfiguration.treatments && Array.isArray(this.pluginConfiguration.treatments)) {
          // console.log(`CONFIG: Loaded ${this.pluginConfiguration.treatments.length} treatment options`);
        }
      },

      // --- Re-added: Handle clicking a day in Month View ---
      selectMonthDay(day) {
        if (!day.isCurrentMonth) return; // Prevent selecting days outside current month

        this.currentDate = moment(day.date).toDate(); // Use moment with the correct timezone
        this.changeView('day'); // Switch to day view
      },

      // --- NEW Time Formatting Helper ---
      formatTime(timeMoment, formatStr = 'h:mm A') {
         // Uses moment passed in or creates a moment object
         if (!moment.isMoment(timeMoment)) {
             console.warn("formatTime expected a moment object, received:", timeMoment);
             // Attempt to parse if it's a simple time string like '9am' or number of minutes
             if (typeof timeMoment === 'string' && this.parseTime(timeMoment) !== null) {
                 const minutes = this.parseTime(timeMoment);
                 timeMoment = moment().startOf('day').add(minutes, 'minutes');
             } else if (typeof timeMoment === 'number') { // Assume minutes
                 timeMoment = moment().startOf('day').add(timeMoment, 'minutes');
             } else {
                 return 'Invalid Time';
             }
         }
         return timeMoment.format(formatStr);
      },

      getStartDate() {
        // Use moment instead of Date for timezone awareness
        const date = moment(this.currentDate);
        if (this.currentView === 'day') {
          return date.toDate();
        }
        if (this.currentView === 'week') {
          // Get first day of week (Monday, or Sunday depending on locale)
          const day = date.day();
          const diff = date.date() - day + (day === 0 ? -6 : 1); // If Sunday, go back to previous Monday
          return date.date(diff).toDate();
        }
        // Month view - get first day of month
        return date.date(1).toDate();
      },
      
      getDayName(date, fullName = false) {
        // Use moment for consistent date handling
        const momentDate = moment(date);
        if (fullName) {
          return momentDate.format('dddd');
        }
        return momentDate.format('ddd');
      },
      
      formatDate(date) {
        // Use moment for consistent date formatting
        return moment(date).format('MMM D');
      },
      
      isToday(date) {
        // Use moment to compare with today in the site's timezone
        const today = moment().startOf('day');
        const momentDate = moment(date).startOf('day');
        return momentDate.isSame(today);
      },
      
      formatHour(hour) {
        // Return a format compatible with parseTime: e.g., "9am", "12pm", "1pm"
        return `${hour % 12 || 12}${hour >= 12 ? 'pm' : 'am'}`;
      },
      formatHourToSlotString(hour) {
        const startHour12 = hour % 12 || 12;
        const startPeriod = hour >= 12 ? 'pm' : 'am';
        const endHour = hour + 1;
        const endHour12 = endHour % 12 || 12;
        const endPeriod = endHour >= 12 ? 'pm' : 'am';
        return `${startHour12}${startPeriod} to ${endHour12}${endPeriod}`;
      },
      getDatesForView() {
        // Return ISO date strings for the visible date range
        return this.visibleDateRange.map(day => day.date);
      },
      fetchAndInitializeData() {
        // First ensure timezone is set
        if (!this.timezoneSet && this.effectiveSiteId) {
          // console.log(`Setting timezone for site ${this.effectiveSiteId}...`);
          this.initializeMomentTimezone();
        }
        // Fetch calendar data
        this.fetchDataForCurrentView();
      },
      fetchDataForCurrentView() {
        const datesToFetch = this.getDatesForView();
        if (datesToFetch.length > 0 && this.effectiveSiteId) {
          // Remove excessive logging
          // console.log(`Fetching availability for site ${this.effectiveSiteId}`);
          this.fetchAvailability({
            dates: datesToFetch,
            siteId: this.effectiveSiteId
          });
        } else if (!this.effectiveSiteId) {
          console.error("Cannot fetch availability: effectiveSiteId is missing from component state.");
        }
      },
      // --- Time Parsing Helper --- 
      parseTime(timeStr) {
        // Handles formats like "8am", "1:30pm", "11:05am"
        if (!timeStr) return null;
        
        const match = timeStr.toLowerCase().match(/(\d{1,2})(:(\d{2}))?(am|pm)/);
        if (!match) {
          console.error(`ERROR: Invalid time format: "${timeStr}"`);
          return null;
        }

        let hours = parseInt(match[1], 10);
        const minutes = parseInt(match[3] || '0', 10);
        const period = match[4];

        if (period === 'pm' && hours !== 12) {
          hours += 12;
        } else if (period === 'am' && hours === 12) { // Midnight case
          hours = 0;
        }
        
        // Return minutes since midnight
        const totalMinutes = hours * 60 + minutes;
        return totalMinutes;
      },

      // --- Slot Calculation (Updated for Overlap Checking) ---
      getSlotsForDay(day) {
        const slots = [];
        const dateISO = day.date; // YYYY-MM-DD
        
        // Use getter to get availability data without excessive logging
        const doctorsAvailability = this.getAvailabilityForDate(dateISO);
        
        // Enhanced validation: check for truly empty days with no doctor slots
        if (!doctorsAvailability) {
            return [];
        }
        
        // Extra validation: verify there are actually doctors with slots for this day
        const doctorNames = Object.keys(doctorsAvailability);
        if (doctorNames.length === 0) {
            return [];
        }
        
        // Double-check that at least one doctor has slots
        let hasAnySlots = false;
        for (const doctorName of doctorNames) {
            const slots = doctorsAvailability[doctorName];
            if (Array.isArray(slots) && slots.length > 0) {
                hasAnySlots = true;
                break;
            }
        }
        
        if (!hasAnySlots) {
            return [];
        }

        // Iterate through time increments instead of businessHours
        for (let increment of this.timeIncrements) {
            const incrementStartMinutes = increment.minutes;
            const incrementEndMinutes = incrementStartMinutes + this.slotIncrementMinutes;
            const startTimeFormatted = increment.formatted;
            const endTimeFormatted = this.formatTime(moment().startOf('day').add(incrementEndMinutes, 'minutes'));
            
            let status = 'booked'; // Default to booked
            let availableDoctorsInIncrement = [];
            let label = startTimeFormatted; // Default label is the start time

            // Check availability only if data exists
            if (doctorsAvailability) {
                for (const doctorName in doctorsAvailability) {
                    const doctorSlotRanges = doctorsAvailability[doctorName];
                    
                    if (Array.isArray(doctorSlotRanges)) {
                        for (const slotRange of doctorSlotRanges) {
                            const rangeParts = slotRange.split(' to ');
                            if (rangeParts.length === 2) {
                                const rangeStartMinutes = this.parseTime(rangeParts[0]);
                                const rangeEndMinutes = this.parseTime(rangeParts[1]);
                                
                                if (rangeStartMinutes !== null && rangeEndMinutes !== null) {
                                    // --- AVAILABILITY CHECK --- :
                                    // The *entire* increment must fall *within* the doctor's range
                                    const isAvailable = incrementStartMinutes >= rangeStartMinutes && incrementEndMinutes <= rangeEndMinutes;
                                    
                                    if (isAvailable) {
                                        if (!availableDoctorsInIncrement.includes(doctorName)) {
                                            availableDoctorsInIncrement.push(doctorName);
                                        }
                                    }
                                }
                            }
                        } // end loop through doctor's ranges
                    }
                } // end loop through doctors
            }

            // Update status and label if any doctor is available for this specific increment
            if (availableDoctorsInIncrement.length > 0) {
                status = 'available';
                label = availableDoctorsInIncrement[0]; // Show first doctor available
                if (availableDoctorsInIncrement.length > 1) {
                    label += ` (+${availableDoctorsInIncrement.length - 1})`;
                }
            }

            slots.push({
                time: incrementStartMinutes, // Use start minutes as unique key
                startTime: startTimeFormatted,
                endTime: endTimeFormatted, // End time of the 30-min slot
                label: label,
                status: status,
                availableDoctors: availableDoctorsInIncrement
            });
        }
        return slots;
      },
      selectSlot(day, slot) {
        this.selectedSlot = {
          dayName: day.dayName,
          dayDate: day.dayDate,
          date: day.date,
          startTimeMinutes: slot.time,
          startTime: slot.startTime,
          endTime: slot.endTime,
          availableDoctors: slot.availableDoctors
        };
        this.bookingForm = {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          treatment: '',
          dentist: '',
          notes: ''
        };
        this.validations = {
          firstName: true,
          lastName: true,
          email: true,
          phone: true,
          treatment: true
        };
        this.showBookingModal = true;
      },
      closeModal() {
        this.showBookingModal = false;
        this.selectedSlot = null;
      },
      confirmBooking() {
        // --- NEW: Validate before proceeding ---
        if (!this.validateForm()) {
            return; // Stop if validation fails
        }

        if (!this.selectedSlot) return;

        this.closeModal();
        // --- REPLACE success message with toast ---
        // this.showSuccessMessage = true;
        // setTimeout(() => { this.showSuccessMessage = false; }, 5000);
        this.$toast.success("Appointment Confirmed! Your appointment has been successfully booked.");

        this.fetchAvailability({
          dates: [this.selectedSlot.date],
          siteId: this.effectiveSiteId
        });
      },
      changeView(view) {
        if (this.currentView === view) return;
        this.currentView = view;
        this.$nextTick(() => {
          this.fetchAndInitializeData();
        });
      },
      prevWeek() {
        // Use moment for consistent date handling
        const date = moment(this.currentDate);
        if (this.currentView === 'day') {
          date.subtract(1, 'days');
        } else if (this.currentView === 'week') {
          date.subtract(7, 'days');
        } else {
          date.subtract(1, 'months').date(1); // First day of previous month
        }
        this.currentDate = date.toDate();
        // console.log(`Navigation: Changed date to ${date.format('YYYY-MM-DD')} (${this.currentView} view)`);
        this.$nextTick(() => {
          this.fetchAndInitializeData();
        });
      },
      nextWeek() {
        // Use moment for consistent date handling
        const date = moment(this.currentDate);
        if (this.currentView === 'day') {
          date.add(1, 'days');
        } else if (this.currentView === 'week') {
          date.add(7, 'days');
        } else {
          date.add(1, 'months').date(1); // First day of next month
        }
        this.currentDate = date.toDate();
        // console.log(`Navigation: Changed date to ${date.format('YYYY-MM-DD')} (${this.currentView} view)`);
        this.$nextTick(() => {
          this.fetchAndInitializeData();
        });
      },
      goToToday() {
        // Use moment to set today with timezone awareness
        this.currentDate = moment().toDate();
        // console.log(`Navigation: Set date to today: ${moment(this.currentDate).format('YYYY-MM-DD')}`);
        this.$nextTick(() => {
          this.fetchAndInitializeData();
        });
      },
      jumpToDate(event) {
        const selectedDate = event.target.value;
        if (selectedDate) {
          this.currentDate = moment(selectedDate).toDate();
          this.$nextTick(() => {
            this.fetchAndInitializeData();
          });
        }
      },
      // --- Helper to get formatted 30-min slot list for a given date ---
      getFormattedSlotsForDay(dateISO) {
          const formattedSlots = [];
          const doctorsAvailability = this.getAvailabilityForDate(dateISO);
          if (!doctorsAvailability) return []; // No data

          for (let increment of this.timeIncrements) {
              const incrementStartMinutes = increment.minutes;
              const incrementEndMinutes = incrementStartMinutes + this.slotIncrementMinutes;
              const startTimeFormatted = increment.formatted;
              const endTimeFormatted = this.formatTime(moment().startOf('day').add(incrementEndMinutes, 'minutes'));
              let availableDoctorsInIncrement = [];

              for (const doctorName in doctorsAvailability) {
                  const doctorSlotRanges = doctorsAvailability[doctorName];
                  if (Array.isArray(doctorSlotRanges)) {
                      for (const slotRange of doctorSlotRanges) {
                          const rangeParts = slotRange.split(' to ');
                          if (rangeParts.length === 2) {
                              const rangeStartMinutes = this.parseTime(rangeParts[0]);
                              const rangeEndMinutes = this.parseTime(rangeParts[1]);
                              if (rangeStartMinutes !== null && rangeEndMinutes !== null) {
                                  if (incrementStartMinutes >= rangeStartMinutes && incrementEndMinutes <= rangeEndMinutes) {
                                      if (!availableDoctorsInIncrement.includes(doctorName)) {
                                          availableDoctorsInIncrement.push(doctorName);
                                      }
                                  }
                              }
                          }
                      }
                  }
              }

              if (availableDoctorsInIncrement.length > 0) {
                  let label = availableDoctorsInIncrement[0];
                  if (availableDoctorsInIncrement.length > 1) {
                      label += ` (+${availableDoctorsInIncrement.length - 1})`;
                  }
                  formattedSlots.push(`${label} ${startTimeFormatted} - ${endTimeFormatted}`);
              }
          }
          return formattedSlots;
      },
      // --- NEW: Form Validation Method ---
      validateForm() {
          let isValid = true;
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Basic email regex

          this.validations = {
              firstName: !!this.bookingForm.firstName,
              lastName: !!this.bookingForm.lastName,
              email: !!this.bookingForm.email && emailRegex.test(this.bookingForm.email),
              phone: !!this.bookingForm.phone, // Basic check: just needs to be non-empty
              treatment: !!this.bookingForm.treatment
          };

          // Check overall validity
          for (const field in this.validations) {
              if (!this.validations[field]) {
                  isValid = false;
                  break;
              }
          }
          
          if (!isValid) {
              this.$toast.error('Please fill out all required fields correctly.');
          }
          return isValid;
      },
      // --- NEW: Updated method to simulate widget click with real plugin ID ---
      simulateWidgetClick() {
        const testPluginId = '9903aae8-2a89-11f0-804a-0abe9b670b4b'; // Real test plugin ID
        // console.log(`PLUGIN: Simulating widget click with test plugin ID: ${testPluginId}`);
        this.$router.push({ 
          path: this.$route.path, 
          query: { pluginId: testPluginId }
        }).catch(err => { 
          // Ignore navigation duplication errors if clicking repeatedly
          if (err.name !== 'NavigationDuplicated') { throw err; }
        });
      },
      // --- NEW: Updated method to determine site ID from plugin ID ---
      async determineEffectiveSiteId() {
        const pluginId = this.$route.query.pluginId;
        let determinedSiteId = null;
        
        if (pluginId) {
          // console.log(`PLUGIN: ID found in URL: ${pluginId}, fetching site info...`);
          try {
            // Fetch site info using the plugin ID
            await this.fetchSiteInfoByPluginId({ pluginId });
            
            // Get site info from store
            const siteInfo = this.getSiteInfo;
            if (siteInfo && siteInfo.siteId) {
              // console.log(`SITE: Info retrieved successfully for site ID: ${siteInfo.siteId}`);
              
              // NEW: Apply plugin configuration settings
              this.applyPluginConfiguration();
              
              // Perform domain validation to prevent unauthorized usage
              const isValidDomain = this.validateDomain(siteInfo.websiteUrl);
              if (!isValidDomain) {
                console.error(`SECURITY: Domain validation failed for plugin ID: ${pluginId}`);
                this.configErrorMessage = `This booking plugin is not authorized for use on this website. Plugin ID: ${pluginId} is restricted to ${siteInfo.websiteUrl}.`;
                this.showConfigurationError = true;
                return;
              }
              
              determinedSiteId = siteInfo.siteId;
              this.showConfigurationError = false;
              // Reset error message to default
              this.configErrorMessage = 'There seems to be an issue loading the appointment calendar for this office. Please ensure the booking link or plugin is correctly configured.';
            } else {
              console.error(`ERROR: No valid site info found for plugin ID: ${pluginId}`);
              this.configErrorMessage = `Invalid plugin ID: ${pluginId}. Please verify your booking plugin configuration.`;
              this.showConfigurationError = true;
            }
          } catch (error) {
            console.error(`ERROR: Failed to fetch site info: ${error.message}`);
            this.configErrorMessage = `Error loading booking configuration: ${error.message}`;
            this.showConfigurationError = true;
          }
        } else {
          // No pluginId - check if user is logged in and use their siteId
          const isLoggedIn = localStorage.getItem('authenticated') === 'true';
          const customerSiteId = this.$store.state.customer?.siteID;

          if (isLoggedIn && customerSiteId) {
            // Logged-in user mode - use their siteId
            console.log(`CALENDAR: Using logged-in user's site ID: ${customerSiteId}`);
            determinedSiteId = customerSiteId;

            // Get timezone from user's settings
            const userTimezone = this.$store.state.customer?.allSettings?.GENERAL_TIMEZONE?.PropertyValue || 'America/New_York';

            // Set site info in store for the component to use
            this.$store.commit('calendar/SET_SITE_INFO', {
              siteId: customerSiteId,
              websiteUrl: null, // Not needed for logged-in users
              timezone: userTimezone
            });

            this.showConfigurationError = false;
          } else {
            // Not logged in and no pluginId - show error
            this.configErrorMessage = 'Please log in to view the appointment calendar, or use a valid booking link.';
            this.showConfigurationError = true;
          }
        }

        // Ensure subsequent fetches use the determined ID
        this.effectiveSiteId = determinedSiteId;
        
        if (!this.effectiveSiteId) {
          console.warn('WARNING: Could not determine site ID, showing configuration error');
        } else {
          // console.log(`SITE: Using site ID: ${this.effectiveSiteId}`);
        }
      },
      // Add a method to validate the current domain against the allowed WebsiteUrl
      validateDomain(allowedWebsiteUrl) {
        // Skip validation in development mode
        if (process.env.NODE_ENV === 'development') {
          // console.log('SECURITY: Domain validation skipped in development environment');
          return true;
        }
        
        // Get current hostname
        const currentHostname = window.location.hostname;
        
        // Handle case where allowed URL might have multiple domains separated by commas
        const allowedUrls = allowedWebsiteUrl.split(',').map(url => url.trim());
        // console.log(`SECURITY: Validating domain ${currentHostname} against ${allowedUrls.length} allowed URLs`);
        
        // Try each allowed URL
        for (const urlStr of allowedUrls) {
          try {
            // Convert allowedWebsiteUrl to just hostname for comparison
            // Add protocol if missing to avoid URL constructor errors
            const normalizedUrl = urlStr.includes('://') ? urlStr : `https://${urlStr}`;
            const allowedUrl = new URL(normalizedUrl);
            const allowedHostname = allowedUrl.hostname;
            
            // Check if current hostname matches or is subdomain of allowed hostname
            const isValid = currentHostname === allowedHostname || 
                            currentHostname.endsWith('.' + allowedHostname);
            
            if (isValid) {
              // console.log(`SECURITY: Domain validation passed for ${allowedHostname}`);
              return true; // Valid match found
            }
          } catch (error) {
            // If URL parsing fails, try simple string comparison for this entry
            console.warn(`SECURITY: Error parsing URL ${urlStr}: ${error.message}`);
            if (currentHostname.includes(urlStr) || urlStr.includes(currentHostname)) {
              // console.log(`SECURITY: Domain validation passed with string comparison for ${urlStr}`);
              return true;
            }
          }
        }
        
        // If we got here, none of the allowed domains matched
        console.error(`SECURITY: Domain validation failed: ${currentHostname} is not in allowed list`);
        return false;
      },
    },
    created() {
      // Set default timezone if not already set
      if (!moment.tz._default) {
        // console.log('TIMEZONE: Setting initial default to America/New_York');
        moment.tz.setDefault('America/New_York');
      }
    },
    watch: {
      '$route.query.pluginId': {
        immediate: true, // Run on initial mount too
        async handler(newPluginId, oldPluginId) {
          // Run on initial load (oldPluginId undefined) or when pluginId changes
          if (newPluginId !== oldPluginId || oldPluginId === undefined) {
            // console.log(`PLUGIN: ID changed from ${oldPluginId || 'none'} to ${newPluginId || 'none'}`);
            await this.determineEffectiveSiteId();

            if (this.effectiveSiteId) {
              // Remove excess log
              this.fetchAndInitializeData();
            }
          }
        }
      }
    }
  };
  </script>

  <style lang="scss" scoped>
  @import './AppointmentCalendarModern.scss';
  </style>

  <style>
  /* Legacy CSS variables for backwards compatibility */
  .appointment-calendar {
    --primary-color: #4361ee;
    --primary-dark: #3a56d4;
    --primary-light: #eef2ff;
    --text-color: #333;
    --text-light: #757575;
    --background-color: #f8f9fa;
    --card-background: #fff;
    --border-color: #e9ecef;
    --border-radius: 8px;
    --shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    --transition: all 0.3s ease;
  }
  
  /* Enhanced Office Header */
  .office-header {
    background-color: var(--card-background);
    padding: 1rem 0; 
    border-bottom: 1px solid var(--border-color);
    margin-bottom: 0.5rem; /* Further reduced bottom margin */
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  }
  
  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .office-info {
    display: flex;
    align-items: center;
    gap: 1rem;
  }
  
  .logo-container {
    flex-shrink: 0;
  }
  
  .logo {
    width: 50px;
    height: 50px;
    background-color: var(--primary-light);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--primary-color);
    font-size: 1.75rem;
  }
  
  .text-content {
    display: flex;
    flex-direction: column;
  }
  
  .office-name {
    font-size: 1.75rem;
    font-weight: 700;
    color: var(--text-color);
    margin: 0;
  }
  
  .office-tagline {
    font-size: 0.875rem;
    color: var(--text-light);
    margin: 0.25rem 0 0 0;
  }
  
  .patient-card {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    background-color: var(--primary-light);
    border-radius: var(--border-radius);
  }
  
  .patient-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background-color: var(--primary-color);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
  }
  
  .patient-info {
    display: flex;
    flex-direction: column;
  }
  
  .patient-name {
    font-weight: 600;
    margin: 0;
    font-size: 0.9375rem;
  }
  
  .patient-status {
    font-size: 0.75rem;
    color: var(--primary-dark);
    margin: 0;
  }
  
  .page-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: var(--text-color);
    margin: 0;
    text-align: center;
  }
  
  .container {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1rem;
  }
  
  /* Calendar Container */
  .calendar-container {
    /* padding-bottom: 1rem; */ 
    /* Allow container to grow vertically */
    flex-grow: 1;
    display: flex; 
    flex-direction: column; 
    overflow: hidden; /* Prevent this container from causing page scroll */
  }

  /* Restore nested container flex grow */
  .calendar-container > .container {
    flex-grow: 1; 
    display: flex; 
    flex-direction: column;
    /* padding-bottom: 0; */ 
  }
  
  .calendar-card {
    background-color: var(--card-background);
    border-radius: var(--border-radius);
    box-shadow: var(--shadow);
    overflow: hidden;
    /* Restore flex grow */
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  }
  
  /* Calendar Controls */
  .calendar-controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 1rem; /* Reduced top/bottom padding */
    border-bottom: 1px solid var(--border-color);
    flex-wrap: wrap;
    gap: 1rem;
  }
  
  .navigation-controls, .view-controls {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .current-month-year {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-color);
    margin: 0;
    min-width: 180px;
    text-align: center;
  }

  .calendar-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .date-picker-wrapper {
    position: relative;
    display: inline-flex;
    align-items: center;
  }

  .date-picker-input {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
    z-index: 1;
  }

  .date-picker-btn {
    pointer-events: none;
  }

  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem 1rem;
    border-radius: var(--border-radius);
    font-weight: 500;
    font-size: 0.875rem;
    cursor: pointer;
    transition: var(--transition);
    border: 1px solid var(--border-color);
    background-color: var(--card-background);
    color: var(--text-color);
  }
  
  .btn-icon {
    padding: 0.5rem;
    border-radius: 50%;
    color: var(--text-light);
  }
  
  .btn-primary {
    background-color: var(--primary-color);
    color: white;
    border-color: var(--primary-color);
  }
  
  .btn-primary:hover {
    background-color: var(--primary-dark);
    border-color: var(--primary-dark);
  }
  
  .btn-secondary {
    background-color: transparent;
    color: var(--text-color);
  }
  
  .btn-secondary:hover {
    background-color: var(--border-color);
  }
  
  .btn-active {
    background-color: var(--primary-light);
    color: var(--primary-dark);
    border-color: var(--primary-light);
  }
  
  .view-controls .btn:not(.btn-active):hover {
    background-color: var(--primary-light);
  }
  
  /* Add a more prominent active button style to match hover */
  .view-controls .btn.btn-active {
    background-color: var(--primary-color);
    color: white;
    border-color: var(--primary-color);
  }
  
  .view-controls .btn.btn-active:hover {
    background-color: var(--primary-dark);
    border-color: var(--primary-dark);
  }
  
  .today-btn {
    margin: 0 0.5rem;
  }
  
  .icon {
    font-size: 1rem;
  }
  
  /* Simple Calendar */
  .simple-calendar {
    padding: 0.5rem 1rem; 
    /* Ensure it's a flex container */
    display: flex; 
    flex-direction: column;
    overflow: hidden; /* Keep this */
  }
  
  .calendar-header {
    display: flex;
    border-bottom: 1px solid var(--border-color);
  }
  
  .calendar-day-header {
    flex: 1;
    padding: 0.25rem 0.5rem;
    text-align: center;
  }
  
  .day-name {
    font-weight: 600;
    text-transform: uppercase;
    font-size: 0.75rem;
    margin-bottom: 0.25rem;
  }
  
  .day-date {
    font-size: 0.875rem;
    color: var(--text-light);
  }
  
  .calendar-body {
    display: flex;
    position: relative;
    /* Remove explicit height, let flexbox handle it */
    /* height: calc(100vh - 170px); */ 
    flex-grow: 1; /* Allow body to fill remaining space in simple-calendar */
    overflow-y: auto;
  }
  
  .time-slots {
    width: 60px;
    flex-shrink: 0;
    padding-top: 0.5rem;
  }
  
  .time-label {
    height: 40px;
    padding: 0.25rem;
    font-size: 0.75rem;
    color: var(--text-light);
    text-align: right;
    padding-right: 0.5rem;
    box-sizing: border-box;
    border-bottom: 1px dotted var(--border-color);
  }
  
  .time-label:not(.half-hour) {
    border-bottom: 1px solid var(--border-color);
    font-weight: 500;
  }
  
  .time-label.half-hour {
    color: #aaa;
  }
  
  .days-grid {
    flex: 1;
    display: flex;
  }
  
  .day-column {
    flex: 1;
    border-left: 1px solid var(--border-color);
  }
  
  .time-slot {
    height: 40px;
    padding: 0.25rem 0.5rem;
    border-bottom: 1px dotted var(--border-color);
    font-size: 0.7rem;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
  }
  
  .day-column > .time-slot:nth-child(2n) {
    border-bottom: 1px solid var(--border-color);
  }
  
  .time-slot.available {
    background-color: var(--primary-color);
    color: white;
    cursor: pointer;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    line-height: 1.1;
    text-align: center;
  }
  
  .slot-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
  
  .slot-doctor {
    font-weight: 700;
    font-size: 0.75rem;
  }
  
  .slot-time {
    font-size: 0.65rem;
    opacity: 0.9;
  }
  
  .time-slot.booked {
    background-color: var(--border-color);
    color: var(--text-light);
  }
  
  /* Updated Modal */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1000;
  }
  
  .modal-backdrop {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: transparent;
  }
  
  .booking-modal {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1001;
    width: 90%;
    max-width: 600px;
  }
  
  /* Increase specificity to override global styles */
  .appointment-calendar .modal-content {
    background-color: #fff;
    border-radius: var(--border-radius);
    box-shadow: var(--shadow);
    overflow: hidden;
    border: 1px solid var(--border-color);
    background-clip: initial;
    outline: none;
  }
  
  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem;
    border-bottom: 1px solid var(--border-color);
    background-color: #fff;
  }
  
  .modal-title {
    font-size: 1.25rem;
    font-weight: 600;
    margin: 0;
  }
  
  .close-btn {
    background: transparent;
    border: none;
    cursor: pointer;
    font-size: 1.5rem;
    line-height: 1;
    color: var(--text-light);
    transition: var(--transition);
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .close-btn:hover {
    color: var(--text-color);
  }
  
  .modal-body {
    padding: 1.25rem;
    background-color: #fff;
  }
  
  /* Date and Time Section */
  .appointment-datetime {
    margin-bottom: 1.5rem;
  }
  
  .datetime-item {
    display: flex;
    align-items: center;
    margin-bottom: 0.75rem;
  }
  
  .datetime-icon {
    width: 24px;
    margin-right: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .datetime-text {
    font-size: 1rem;
    font-weight: 500;
  }
  
  /* Form Styling */
  .appointment-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  
  .form-row {
    display: flex;
    gap: 1rem;
  }
  
  .form-group {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
  
  label {
    font-size: 0.875rem;
    font-weight: 500;
    margin-bottom: 0.375rem;
    color: var(--text-color);
  }
  
  .form-control {
    padding: 0.625rem 0.75rem;
    border: 1px solid var(--border-color);
    border-radius: var(--border-radius);
    font-size: 0.9375rem;
    transition: var(--transition);
    width: 100%;
  }
  
  .form-control:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(67, 97, 238, 0.1);
  }
  
  select.form-control {
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23757575' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 0.75rem center;
    background-size: 16px;
    padding-right: 2.5rem;
  }
  
  textarea.form-control {
    min-height: 100px;
    resize: vertical;
  }
  
  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    padding: 1.25rem;
    border-top: 1px solid var(--border-color);
    background-color: #fff;
  }
  
  .book-btn {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
  
  /* REMOVED Success Message Styles */
  /* 
  .success-message {
    position: fixed;
    bottom: 2rem;
    left: 0;
    width: 100%;
    z-index: 1000;
    animation: slideUp 0.3s ease-out forwards;
  }
  
  .success-content {
    background-color: var(--primary-color);
    color: white;
    border-radius: var(--border-radius);
    box-shadow: var(--shadow);
    padding: 1rem;
    display: flex;
    align-items: center;
    gap: 1rem;
  }
  
  .success-icon {
    font-size: 1.5rem;
    background-color: rgba(255, 255, 255, 0.2);
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .success-text {
    flex: 1;
  }
  
  .success-text h3 {
    margin: 0 0 0.25rem 0;
    font-size: 1.125rem;
    font-weight: 600;
  }
  
  .success-text p {
    margin: 0;
    font-size: 0.875rem;
    opacity: 0.9;
  }
  
  .success-message .close-btn {
    color: white;
    opacity: 0.8;
  }
  
  .success-message .close-btn:hover {
    opacity: 1;
  }
  
  @keyframes slideUp {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  */

  /* --- NEW: Validation Styles --- */
  .form-control.is-invalid {
      border-color: #dc3545; /* Bootstrap danger color */
  }
  .text-danger {
      color: #dc3545 !important; /* Bootstrap danger color */
      font-size: 0.8em;
      margin-top: 0.25rem;
  }

  /* NEW: Info text color for treatment duration */
  .text-info {
      color: #17a2b8 !important; /* Bootstrap info color */
      font-size: 0.8em;
      display: block;
  }
  .mt-1 {
      margin-top: 0.25rem;
  }

  /* Responsive Styles */
  @media (max-width: 768px) {
    .header-content {
      flex-direction: column;
      align-items: flex-start;
      gap: 1rem;
    }
    
    .patient-card {
      align-self: stretch;
    }
    
    .calendar-controls {
      flex-direction: column;
      align-items: flex-start;
    }
    
    .view-controls {
      width: 100%;
      justify-content: space-between;
    }
    
    .calendar-body {
      flex-direction: column;
    }
    
    .time-slots {
      width: 100%;
      display: none;
    }
    
    .days-grid {
      flex-direction: column;
    }
    
    .day-column {
      border-left: none;
      border-top: 1px solid var(--border-color);
    }
    
    .time-slot {
      position: relative;
      padding-left: 60px;
    }
    
    .time-slot:before {
      content: attr(data-time);
      position: absolute;
      left: 0;
      top: 0;
      width: 60px;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      padding-right: 0.5rem;
      font-size: 0.75rem;
      color: var(--text-light);
      background-color: var(--background-color);
    }
    
    .form-row {
      flex-direction: column;
      gap: 1rem;
    }
  }
  
  @media (max-width: 480px) {
    .btn {
      padding: 0.375rem 0.75rem;
      font-size: 0.8125rem;
    }
    
    .modal-title {
      font-size: 1.125rem;
    }
    
    .booking-modal {
      width: 95%;
    }
    
    .datetime-text {
      font-size: 0.9375rem;
    }
  }

  /* Add styles for loading/error indicators if needed */
  .calendar-loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
  }

  .calendar-error-message {
    color: red;
    background-color: #ffebee;
    border: 1px solid red;
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: var(--border-radius);
  }

  /* --- Re-added: Month View Styles --- */
  .month-calendar {
    /* Ensure it grows within calendar-card */
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden; 
  }

  .month-header {
    display: flex;
    padding: 0 0.5rem; /* Align with day padding */
    border-bottom: 1px solid var(--border-color);
    flex-shrink: 0; /* Prevent header from shrinking */
  }

  .month-day-name {
    flex: 1 0 0; /* Equal width columns */
    padding: 0.5rem 0.25rem;
    text-align: center;
    font-weight: 600;
    font-size: 0.8rem;
  }

  .month-grid {
    /* Allow grid to fill remaining space */
    flex-grow: 1; 
    display: flex;
    flex-direction: column; /* Weeks stack vertically */
    border-left: 1px solid var(--border-color);
    border-top: 1px solid var(--border-color); 
    /* overflow: hidden; */ /* Move overflow handling here */
    overflow-y: auto; /* Allow grid itself to scroll if needed */
  }

  .month-week {
    flex: 1 0 0; /* Equal height rows */
    display: flex;
    min-height: 60px; /* Minimum row height */
  }

  .month-day {
    flex: 1 0 0; /* Equal width columns */
    padding: 0.5rem 0.25rem;
    /* text-align: right; */ /* Let flex handle alignment */
    border-right: 1px solid var(--border-color);
    border-bottom: 1px solid var(--border-color);
    cursor: pointer;
    position: relative; 
    transition: background-color 0.2s ease;
    overflow: hidden;
    /* Use flex to position number and slots */
    display: flex;
    flex-direction: column; 
    justify-content: flex-start; /* Align content to top */
    align-items: stretch; /* Stretch children width */
  }
  
  .month-day:hover {
      background-color: #f0f0f0;
  }

  .month-day.not-current-month {
    background-color: #f8f9fa; /* Slightly different background */
    color: #adb5bd; /* Greyed out text */
    cursor: default;
  }
  
  .month-day.not-current-month:hover {
      background-color: #f8f9fa; /* Don't highlight on hover */
  }

  .day-number {
    font-size: 0.8rem;
    font-weight: 500;
    text-align: right; /* Keep day number aligned right */
    padding-bottom: 0.25rem; /* Space below number */
    flex-shrink: 0; /* Prevent number from shrinking */
  }

  .month-day.is-today .day-number {
    background-color: #3abf94;
    color: white;
    border-radius: 50%;
    width: 1.5em;
    height: 1.5em;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    font-weight: 700;
    padding: 0;
  }

  /* --- Styles for slot list in month day --- */
  .month-day-slots {
      flex-grow: 1; /* Allow slot list to take remaining space */
      overflow-y: auto; /* Enable scrolling if slots overflow */
      text-align: left; /* Align slot text left */
      margin-top: 0.25rem;
      align-self: flex-start; /* Don't stretch to full width - let slots be compact */
  }

  .month-slot-item {
      display: inline-block;
      font-size: 0.5625rem;
      line-height: 1.3;
      padding: 2px 6px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-bottom: 2px;
      margin-right: 2px;
      background-color: rgba(58, 191, 148, 0.15);
      border-radius: 10px;
      color: #2a8f70;
      font-weight: 500;
      max-width: 100%;
  }

  /* --- Remove or modify old availability styles --- */
  /* .availability-dot {
      position: absolute;
      bottom: 5px;
      left: 50%;
      transform: translateX(-50%);
      width: 6px;
      height: 6px;
      background-color: var(--primary-dark);
      border-radius: 50%;
  } */
  
  /* Optional: Different style if it also has availability */
  .month-day.has-availability {
  }
  .month-day.has-availability.not-current-month {
  }

  /* --- Configuration Error Styles --- */
  .configuration-error {
      text-align: center;
      padding: 4rem 1rem;
      background-color: #fff7cd; /* Light yellow */
      border: 1px solid #ffeeba; /* Yellow border */
      border-radius: var(--border-radius);
      margin-top: 2rem;
  }
  .configuration-error .error-icon {
      font-size: 3rem;
      margin-bottom: 1rem;
  }
  .configuration-error h2 {
      color: #856404; /* Dark yellow/brown */
      margin-bottom: 1rem;
  }
  
  /* Admin Button (Simple version) */
  .admin-link {
    margin-left: auto;
    display: flex;
    align-items: center;
  }
  
  .admin-button {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    background-color: #f8f9fa;
    border: 1px solid var(--border-color);
    border-radius: var(--border-radius);
    color: var(--text-color);
    text-decoration: none;
    font-size: 0.875rem;
    font-weight: 500;
    transition: all 0.2s ease;
  }
  
  .admin-button:hover {
    background-color: var(--primary-light);
    color: var(--primary-dark);
  }
  
  .admin-icon {
    font-size: 1.125rem;
  }
  </style>